/**
 * Préfixe / normalise les chemins médias avec VITE_APP_CDN_BASE_URL pour que le
 * navigateur ne les charge pas depuis l’origine Vite (:5173) ni depuis des hosts
 * Docker internes (api-django:8080) inaccessibles depuis le navigateur.
 *
 * - Local (CDN = Django :8060) : chemins sous MEDIA_URL (`/media/…`).
 * - CDN distant (S3/CloudFront) : clés à la racine (`/graphics/…`, `/flags/…`).
 */

function cdnBase(): string {
  return String(import.meta.env.VITE_APP_CDN_BASE_URL ?? '').replace(/\/$/, '')
}

function isLocalDjangoCdn(cdn: string): boolean {
  if (!cdn) {
    return false
  }
  try {
    const { hostname, port } = new URL(cdn)
    const h = hostname.toLowerCase()
    const loopback = h === 'localhost' || h === '127.0.0.1' || h === '[::1]'
    return loopback && (port === '8060' || port === '8080')
  } catch {
    return false
  }
}

/** Hosts Docker Compose injoignables depuis le navigateur. */
function isDockerInternalHost(hostname: string): boolean {
  const h = hostname.toLowerCase()
  return h === 'api-django' || h === 'api-ts' || h === 'prediction'
}

/**
 * Aligne le pathname sur la convention du CDN actif.
 * Local Django sert sous `/media/`; le bucket CDN distant est à la racine.
 */
export function normalizeMediaPathForCdn(pathname: string, cdn: string = cdnBase()): string {
  let path = pathname.startsWith('/') ? pathname : `/${pathname}`
  if (isLocalDjangoCdn(cdn)) {
    if (/^\/(flags|graphics|images|pdfs|captures)(\/|$)/i.test(path)) {
      path = `/media${path}`
    }
  } else if (cdn && path.startsWith('/media/')) {
    path = path.slice('/media'.length)
    if (!path.startsWith('/')) {
      path = `/${path}`
    }
  }
  return path
}

export function resolveCdnMediaUrl(src: string): string {
  if (!src) {
    return src
  }
  const trimmed = src.trim()
  const cdn = cdnBase()

  let pathname: string
  let search = ''

  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('//')) {
    try {
      const absolute = trimmed.startsWith('//') ? `http:${trimmed}` : trimmed
      const u = new URL(absolute)
      if (!isDockerInternalHost(u.hostname)) {
        return trimmed
      }
      pathname = u.pathname
      search = u.search
    } catch {
      return trimmed
    }
  } else if (trimmed.startsWith('/')) {
    const q = trimmed.indexOf('?')
    pathname = q === -1 ? trimmed : trimmed.slice(0, q)
    search = q === -1 ? '' : trimmed.slice(q)
  } else {
    const q = trimmed.indexOf('?')
    const bare = q === -1 ? trimmed : trimmed.slice(0, q)
    pathname = `/${bare}`
    search = q === -1 ? '' : trimmed.slice(q)
  }

  const normalized = normalizeMediaPathForCdn(pathname, cdn)
  return cdn ? `${cdn}${normalized}${search}` : `${normalized}${search}`
}

/** Country / riding flags live under `/flags/{code}.jpg` on the CDN (or `/media/flags/` locally). */
export function resolveFlagCdnUrl(codeOrFilename: string): string {
  const file = codeOrFilename.replace(/^.*\//, '').toLowerCase()
  const base = file.replace(/\.(webp|avif|jpg|jpeg|png|svg)$/i, '')
  return resolveCdnMediaUrl(`/flags/${base}.jpg`)
}

/**
 * Vite eager imports resolve to `/src/assets/images/flags/…` in dev (and hashed `/assets/…` in
 * build). Those paths must not be prefixed with the Django CDN base as-is — extract the flag file.
 */
export function rewriteFlagAssetUrl(image: string): string | null {
  const match = image.match(
    /(?:\/(?:src\/)?assets\/images\/flags\/|\/media\/flags\/|\/flags\/)([^/?#]+)/i,
  )
  if (!match) {
    return null
  }
  return resolveFlagCdnUrl(match[1])
}
